import { Component, EventEmitter, Input, OnChanges, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { IProduct } from 'src/app/models/products/product';
import { ProductValidationDialogComponent, ProductValidationDialogData } from './product-validation-dialog.component';

export type FormatType = 'stills' | 'videos' | 'json';

export interface PatternInfo {
  pattern: string;
  type: FormatType;
  formatName: string;
  extension: string;
  from: number;
  to: number | null;
  indexOffset: number;
}

export interface FilenamePreview {
  pattern: string;
  substituted: string;
  valid: boolean;
  extension?: string;
  type?: FormatType;
  formatName?: string;
  from?: number;
  to?: number | null;
  index?: number;
  indexOffset?: number;
}

export interface ProductValidation {
  product: IProduct;
  valid: boolean;
  errors: string[];
  previews: FilenamePreview[];
}

export type ProductDataIdentifier = 'barcode' | 'reference' | 'description';

@Component({
  selector: 'app-product-validation-list',
  templateUrl: './product-validation-list.component.html',
  styleUrls: ['./product-validation-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class ProductValidationListComponent implements OnInit, OnChanges {
  @Input() products: IProduct[] = [];
  @Input() validateProductDataExport = false;
  @Input() productDataFormats: readonly string[] = ['json'];
  @Input() productDataIdentifier: ProductDataIdentifier = 'barcode';
  @Input() expectedAssets = 0;
  @Input() organizationId?: string;
  @Output() validationComplete = new EventEmitter<boolean>();

  validations: ProductValidation[] = [];
  expandedProducts: Set<string> = new Set();

  constructor(private dialog: MatDialog) {}

  ngOnInit(): void {
    this.validateProducts();
  }

  ngOnChanges(): void {
    this.validateProducts();
  }

  toggleExpand(productId: string): void {
    if (this.expandedProducts.has(productId)) {
      this.expandedProducts.delete(productId);
    } else {
      this.expandedProducts.add(productId);
    }
  }

  isExpanded(productId: string): boolean {
    return this.expandedProducts.has(productId);
  }

  openValidationDialog(): void {
    const data: ProductValidationDialogData = {
      validations: this.validations,
      totalProducts: this.products.length,
      expectedAssets: this.expectedAssets,
      organizationId: this.organizationId,
      validateProductDataExport: this.validateProductDataExport,
    };
    this.dialog.open(ProductValidationDialogComponent, {
      data,
      width: 'auto',
      maxWidth: '95vw',
      maxHeight: '85vh',
    });
  }

  get invalidProductsCount(): number {
    return this.validations.filter((v) => !v.valid).length;
  }

  validateProducts(): void {
    const patterns = this.getPatterns();

    const barcodeCounts: ReadonlyMap<string, number> = this.validateProductDataExport
      ? this.products.reduce(
          (acc, p) => {
            const bc = (p.barcode ?? '').trim();
            return bc ? new Map(acc).set(bc, (acc.get(bc) ?? 0) + 1) : acc;
          },
          new Map<string, number>()
        )
      : new Map<string, number>();

    this.validations = this.products.map((product) => {
      const labelErrors: string[] = this.validateProductDataExport
        ? [
            ...(!(product.barcode?.trim()) ? ['Missing barcode for JSON export'] : []),
            ...((product.barcode?.trim() && (barcodeCounts.get(product.barcode.trim()) ?? 0) > 1)
              ? [`Duplicate barcode '${product.barcode.trim()}' — JSON files will overwrite`]
              : []),
          ]
        : [];

      const previews: FilenamePreview[] = patterns.flatMap((patternInfo) => {
        const indices = this.getIndicesForPattern(patternInfo, product);
        return indices.map((index) => {
          const substituted = this.substitutePattern(patternInfo.pattern, product, patternInfo.extension, index, patternInfo.indexOffset);
          const startsValid = /^[a-zA-Z0-9]/.test(substituted);
          return {
            pattern: patternInfo.pattern,
            substituted,
            valid: startsValid,
            extension: patternInfo.extension,
            type: patternInfo.type,
            formatName: patternInfo.formatName,
            from: patternInfo.from,
            to: patternInfo.to,
            index,
            indexOffset: patternInfo.indexOffset,
          };
        });
      });

      const patternErrors: string[] = previews
        .filter((p) => !p.valid)
        .map((p) => {
          const firstToken = this.getFirstToken(p.pattern);
          const fieldValue = firstToken ? product[firstToken as keyof IProduct] : '';
          return `'${p.substituted}' starts with invalid character (from ${firstToken}: '${fieldValue}')`;
        });

      const errors = [...labelErrors, ...patternErrors];
      return { product, valid: errors.length === 0, errors, previews };
    });

    const allValid = this.validations.every((v) => v.valid);
    this.validationComplete.emit(allValid);
  }

  getPatterns(): PatternInfo[] {
    if (!this.validateProductDataExport) {
      return [];
    }

    const identifierToken: string = `{${this.productDataIdentifier}}`;
    return this.productDataFormats.map((extension: string) => ({
      pattern: `${identifierToken}.${extension.toLowerCase()}`,
      type: 'json' as FormatType,
      formatName: 'Product Data',
      extension: extension.toLowerCase(),
      from: 1,
      to: null,
      indexOffset: 0,
    }));
  }

  substitutePattern(pattern: string, product: IProduct, extension: string = 'jpg', index: number = 1, indexOffset: number = 0): string {
    const barcode = (product.barcode || '').replace(/ /g, '_');
    const reference = (product.reference || '').replace(/ /g, '_');
    const description = (product.description || '').replace(/ /g, '_');

    return pattern
      .replace(/\{barcode\}/g, barcode)
      .replace(/\{reference\}/g, reference)
      .replace(/\{description\}/g, description)
      .replace(/\{index\}/g, (index + indexOffset).toString())
      .replace(/\{alphabetical-index\}/g, String.fromCharCode(index + 96 + indexOffset))
      .replace(/\{extension\}/g, extension);
  }

  private getIndicesForPattern(patternInfo: PatternInfo, product: IProduct): number[] {
    if (patternInfo.type === 'json') {
      return [1];
    }

    const from = patternInfo.from;
    const to = patternInfo.to;

    if (to !== null) {
      const count = Math.max(1, to - from + 1);
      return Array.from({ length: count }, (_, i) => from + i);
    }

    const assetCount = patternInfo.type === 'stills'
      ? (product.postAssetStillsCount ?? 0)
      : (product.postAssetVideosCount ?? 0);

    const upperBound = Math.max(from, assetCount);
    const count = upperBound - from + 1;
    return Array.from({ length: count }, (_, i) => from + i);
  }

  getFirstToken(pattern: string): string | null {
    const match = pattern.match(/^\{(barcode|reference|description)\}/);
    return match ? match[1] : null;
  }

  getFieldsToValidate(): Set<string> {
    const fields = new Set<string>();

    if (this.validateProductDataExport) {
      fields.add(this.productDataIdentifier);
    }

    return fields;
  }
}
